import {
  Badge,
  Banner,
  Button,
  Empty,
  Grid,
  GridItem,
  Input,
  Loader,
  Select,
  Switch,
  Table,
  Text,
  Textarea,
} from '@cloudflare/kumo'
import { ClipboardText, DownloadSimple, Factory, PencilSimple, Plus } from '@phosphor-icons/react'
import {
  FACTORY_STATUSES,
  PRODUCT_CATEGORY_IDS,
  QUOTE_STORAGE_TYPES,
  countFactoriesByMajor,
  factoryInputSchema,
  findMajor,
  productCategoryLabel,
  type AdminFactoryReplyView,
  type FactoryInput,
  type FactoryStatus,
  type FactoryView,
} from '@repo/shared'
import { useCallback, useEffect, useState } from 'react'
import { factoriesApi, useFactoryRepliesFeature } from '../factories-api'
import { BulkInviteButton, FactoryIntakePanel, PendingReplies } from '../components/factory-intake'
import { FactoryTaxonomyEditor } from '../components/factory-taxonomy-editor'
import { HelpPanel } from '../components/help-panel'
import { PageHeader } from '../components/page-header'
import { useGetToken } from '../lib/auth'
import { downloadCsv } from '../lib/download'
import {
  EMPTY_FACTORY_FILTER,
  MISSING_INFO,
  factoryTaxonomyText,
  filterFactories,
  mailTargets,
  mailTargetsCsv,
  type FactoryFilter,
  type MissingInfoKey,
} from '../lib/factory-filters'
import { toMessage } from '../lib/format'
import { FACTORY_STATUS_LABEL, STORAGE_LABEL } from '../lib/quote-labels'

const STATUS_ITEMS = FACTORY_STATUSES.map((s) => ({ label: FACTORY_STATUS_LABEL[s], value: s }))
const STATUS_BADGE = {
  lead: 'outline',
  replied: 'info',
  negotiating: 'info',
  partner: 'success',
  inactive: 'outline',
  do_not_contact: 'error',
} as const

const EMPTY: FactoryInput = factoryInputSchema.parse({ name: '-' })
const toInput = ({
  id: _i,
  sourceRef: _s,
  capabilities: _cap,
  createdAt: _c,
  updatedAt: _u,
  ...rest
}: FactoryView): FactoryInput => rest

/** '한빛, 경기' 처럼 쉼표로 적은 칸 → 배열 (빈 칸은 뺀다) */
const splitList = (text: string) =>
  text
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean)

/**
 * 공장 목록 (로드맵 1위, 대표 결정 10/30: 영업 리스트 전체를 넣고 파트너만 필터로 추린다). 견적 상세의 '새 공장 견적' 에서
 * 여기 공장을 고르면 공장명·소재지·인증이 채워진다. 지우기는 없다 — 협력이 끝나면 '중단', 수신거부면 '연락 금지'
 */
export function FactoriesPage() {
  const getToken = useGetToken()
  const [items, setItems] = useState<FactoryView[] | null>(null)
  const [filter, setFilter] = useState<FactoryFilter>(EMPTY_FACTORY_FILTER)
  const [missing, setMissing] = useState<MissingInfoKey[]>([])
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /** null = 폼 닫힘, '' = 새 공장, 그 밖 = 고치는 공장 id */
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<FactoryInput>({ ...EMPTY, name: '' })
  /** 공장 회신 받기 (로드맵 8번). 두 겹 스위치가 켜졌을 때만 설비·단가 버튼·검토 대기 목록·일괄 초대를 그린다 */
  const repliesOn = useFactoryRepliesFeature()
  const [intakeId, setIntakeId] = useState<string | null>(null)
  const [pending, setPending] = useState<AdminFactoryReplyView[]>([])

  const reload = useCallback(async () => {
    try {
      setItems((await factoriesApi.list(getToken)).items)
      setError(null)
    } catch (err) {
      setError(toMessage(err))
    }
  }, [getToken])

  const reloadPending = useCallback(async () => {
    if (!repliesOn) return
    try {
      setPending((await factoriesApi.pendingReplies(getToken)).items)
    } catch (err) {
      setError(toMessage(err))
    }
  }, [getToken, repliesOn])

  useEffect(() => {
    void reloadPending()
  }, [reloadPending])

  useEffect(() => {
    void reload()
  }, [reload])

  const open = (factory: FactoryView | null) => {
    setNotice(null)
    setEditingId(factory?.id ?? '')
    setForm(factory ? toInput(factory) : { ...EMPTY, name: '' })
  }

  const save = async () => {
    if (editingId === null) return
    setBusy(true)
    try {
      const input = factoryInputSchema.parse(form)
      const { factory } = editingId
        ? await factoriesApi.update(getToken, editingId, input)
        : await factoriesApi.create(getToken, input)
      setNotice(
        editingId ? `'${factory.name}' 을(를) 고쳤어요.` : `'${factory.name}' 을(를) 더했어요.`,
      )
      setEditingId(null)
      await reload()
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const set = (patch: Partial<FactoryFilter>) => setFilter((f) => ({ ...f, ...patch }))
  const shown = filterFactories(items ?? [], filter)
  const targets = mailTargets(shown, missing)
  const counts = countFactoriesByMajor(shown)
  const editingFactory = items?.find((f) => f.id === editingId)

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="공장 목록"
        description="영업 리스트 전체와 파트너 공장이에요. 관리자만 보고, 앱에는 견적에 적힌 공장명·소재지·인증만 보여요."
        actions={
          <Button variant="primary" icon={<Plus />} onClick={() => open(null)}>
            공장 더하기
          </Button>
        }
      />
      <HelpPanel
        id="factories"
        steps={[
          <>단계·분류·지역·인증으로 거르세요. 파트너만 보려면 '파트너만'을 켜세요.</>,
          <>
            견적 상세의 <b>새 공장 견적</b> 에서 공장을 고르면 공장명·소재지·인증이 채워져요.
          </>,
          <>
            <b>상세 확인 메일 대상</b> 에서 비어 있는 정보를 골라 내려받으세요. 연락 금지·중단
            공장은 늘 빠져요. 메일은 대표 승인 뒤에만 보내요.
          </>,
        ]}
        notes={[
          '이미 보낸 견적은 공장 정보를 고쳐도 바뀌지 않아요.',
          '전화·메일은 회사 대표번호와 대표 메일만 적어 주세요. 담당자 휴대폰·개인 메일은 적지 않아요.',
          "'연락 금지'는 수신거부·수집거부 표시라 다른 단계로 바꿀 수 없어요.",
        ]}
      />
      {error && <Banner variant="error" title="문제가 생겼어요" description={error} />}
      {notice && <Banner variant="default" title={notice} />}

      {repliesOn && (
        <PendingReplies
          items={pending}
          onChanged={async () => {
            await Promise.all([reload(), reloadPending()])
          }}
        />
      )}
      {repliesOn && intakeId && items?.some((f) => f.id === intakeId) && (
        <FactoryIntakePanel
          key={intakeId}
          factory={items.find((f) => f.id === intakeId)!}
          onChanged={reload}
        />
      )}

      {editingId !== null && (
        <FactoryForm
          form={form}
          setForm={setForm}
          editing={editingId !== ''}
          locked={editingFactory?.status === 'do_not_contact'}
          busy={busy}
          onSave={() => void save()}
          onCancel={() => setEditingId(null)}
        />
      )}

      <div className="flex flex-wrap items-end gap-3" data-testid="factory-filters">
        <div className="min-w-48 flex-1">
          <Input
            label="찾기"
            placeholder="공장명, 지역, 품목"
            value={filter.q}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ q: e.target.value })}
          />
        </div>
        <Select
          label="단계"
          value={filter.status}
          onValueChange={(v: string | null) => set({ status: (v ?? '') as FactoryStatus | '' })}
          items={[{ label: '모든 단계', value: '' }, ...STATUS_ITEMS]}
        />
        <Input
          label="지역"
          placeholder="예: 경기"
          value={filter.region}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ region: e.target.value })}
        />
        <Input
          label="인증"
          placeholder="예: HACCP"
          value={filter.certification}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            set({ certification: e.target.value })
          }
        />
        <Switch
          label="파트너만"
          checked={filter.partnerOnly}
          onCheckedChange={(checked: boolean) => set({ partnerOnly: checked })}
        />
        <Switch
          label="분류 확인 필요만"
          checked={filter.needsReview}
          onCheckedChange={(checked: boolean) => set({ needsReview: checked })}
        />
      </div>

      {counts.length > 0 && (
        <div className="flex flex-col gap-2" data-testid="factory-category-counts">
          <Text variant="secondary" size="sm">
            분류별 공장 수 (지금 거른 {shown.length}곳 기준, 누르면 그 분류만)
          </Text>
          <div className="flex flex-wrap gap-2">
            {filter.major && (
              <Button variant="ghost" size="sm" onClick={() => set({ major: '' })}>
                분류 거르기 풀기
              </Button>
            )}
            {counts.map((c) => {
              const key = `${c.axis}:${c.majorId}`
              return (
                <Button
                  key={key}
                  size="sm"
                  variant={filter.major === key ? 'primary' : 'secondary'}
                  onClick={() => set({ major: filter.major === key ? '' : key })}
                >
                  {c.axis === 'menu' ? '메뉴 ' : ''}
                  {findMajor(c.axis, c.majorId)?.ko ?? c.majorId} {c.count}
                </Button>
              )
            })}
          </div>
        </div>
      )}

      <div
        className="border-kumo-line flex flex-wrap items-center gap-3 rounded-lg border p-4"
        data-testid="factory-mail-targets"
      >
        <Text size="sm">상세 확인 메일 대상</Text>
        {(Object.keys(MISSING_INFO) as MissingInfoKey[]).map((k) => (
          <label key={k} className="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              checked={missing.includes(k)}
              onChange={() =>
                setMissing((m) => (m.includes(k) ? m.filter((x) => x !== k) : [...m, k]))
              }
            />
            {MISSING_INFO[k].label} 비어 있음
          </label>
        ))}
        <Button
          variant="secondary"
          icon={<DownloadSimple />}
          disabled={targets.length === 0}
          onClick={() => downloadCsv('factory-mail-targets.csv', mailTargetsCsv(targets))}
        >
          {targets.length}곳 내려받기
        </Button>
        {repliesOn && <BulkInviteButton targets={targets} />}
        <Text variant="secondary" size="xs">
          연락 금지·중단 공장과 메일·문의폼이 없는 공장은 빠져요. 발송은 하지 않아요.
        </Text>
      </div>

      {items === null ? (
        <Loader />
      ) : shown.length === 0 ? (
        <Empty
          icon={<Factory size={40} className="text-kumo-inactive" />}
          title={items.length === 0 ? '등록한 공장이 없어요' : '찾는 공장이 없어요'}
        />
      ) : (
        <div className="overflow-x-auto">
          <Text variant="secondary" size="sm">
            {shown.length}곳 / 전체 {items.length}곳
          </Text>
          <Table data-testid="factory-table">
            <Table.Header>
              <Table.Row>
                <Table.Head>공장</Table.Head>
                <Table.Head>분류</Table.Head>
                <Table.Head>최소 주문량</Table.Head>
                <Table.Head>인증</Table.Head>
                <Table.Head>단계</Table.Head>
                <Table.Head />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {shown.map((f) => (
                <Table.Row key={f.id}>
                  <Table.Cell>
                    <div className="font-medium">{f.name}</div>
                    <Text variant="secondary" size="xs">
                      {[f.country, f.region].filter(Boolean).join(' · ')}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    {factoryTaxonomyText(f.taxonomyKeys) || '—'}
                    {f.taxonomyNeedsReview && (
                      <>
                        {' '}
                        <Badge variant="warning">확인 필요</Badge>
                      </>
                    )}
                  </Table.Cell>
                  <Table.Cell className="max-w-64 break-words">{f.moqNote || '—'}</Table.Cell>
                  <Table.Cell>{f.certifications.join(', ') || '—'}</Table.Cell>
                  <Table.Cell>
                    <Badge variant={STATUS_BADGE[f.status]}>{FACTORY_STATUS_LABEL[f.status]}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<PencilSimple />}
                        onClick={() => open(f)}
                      >
                        고치기
                      </Button>
                      {repliesOn && (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<ClipboardText />}
                          onClick={() => setIntakeId(intakeId === f.id ? null : f.id)}
                        >
                          설비·단가
                        </Button>
                      )}
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </div>
  )
}

type TextKey =
  | 'name'
  | 'region'
  | 'items'
  | 'moqNote'
  | 'leadTimeNote'
  | 'website'
  | 'phone'
  | 'email'
  | 'contactFormUrl'
  | 'notes'

function FactoryForm({
  form,
  setForm,
  editing,
  locked,
  busy,
  onSave,
  onCancel,
}: {
  form: FactoryInput
  setForm: (f: FactoryInput) => void
  editing: boolean
  /** 연락 금지 공장: 단계를 바꾸지 못한다 (서버도 409) */
  locked: boolean
  busy: boolean
  onSave: () => void
  onCancel: () => void
}) {
  // 인증은 입력 중인 글자를 그대로 들고 있는다 — 배열을 다시 합쳐 value 로 쓰면 방금 친 쉼표가 지워진다 (offer-form 과 같음)
  const [certText, setCertText] = useState(() => form.certifications.join(', '))
  const text = (key: TextKey) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [key]: e.target.value })
  const toggle = <T extends string>(list: readonly T[], value: T) =>
    list.includes(value) ? list.filter((x) => x !== value) : [...list, value]
  const valid = factoryInputSchema.safeParse(form).success

  return (
    <div
      className="border-kumo-line flex flex-col gap-5 rounded-lg border p-5"
      data-testid="factory-form"
    >
      <Text variant="heading">{editing ? '공장 고치기' : '새 공장'}</Text>
      <Grid variant="2up" gap="sm">
        <GridItem>
          <Input label="공장명 *" value={form.name} onChange={text('name')} />
        </GridItem>
        <GridItem>
          <Input
            label="소재지"
            placeholder="예: 경기 화성"
            description="견적의 소재지로 그대로 들어가요."
            value={form.region}
            onChange={text('region')}
          />
        </GridItem>
        <GridItem>
          <Input
            label="나라 (두 글자)"
            placeholder="KR"
            value={form.country}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm({ ...form, country: e.target.value.toUpperCase() })
            }
          />
        </GridItem>
        <GridItem>
          <Select
            label="단계"
            description={locked ? '연락 금지는 바꿀 수 없어요 (수신거부·수집거부).' : undefined}
            disabled={locked}
            value={form.status}
            onValueChange={(v: string | null) =>
              v && setForm({ ...form, status: v as FactoryStatus })
            }
            items={STATUS_ITEMS}
          />
        </GridItem>
        <GridItem>
          <Input
            label="공장 인증 (쉼표로 구분)"
            placeholder="HACCP, FSSC 22000"
            value={certText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setCertText(e.target.value)
              setForm({ ...form, certifications: splitList(e.target.value).slice(0, 10) })
            }}
          />
        </GridItem>
        <GridItem>
          <Input
            label="최소 주문량 (원문)"
            placeholder="예: 액상 400kg, 분말 200kg"
            value={form.moqNote}
            onChange={text('moqNote')}
          />
        </GridItem>
        <GridItem>
          <Input label="개발·생산 기간" value={form.leadTimeNote} onChange={text('leadTimeNote')} />
        </GridItem>
        <GridItem>
          <Input label="홈페이지" value={form.website} onChange={text('website')} />
        </GridItem>
        <GridItem>
          <Input
            label="대표번호"
            description="회사 대표번호만 적어요."
            value={form.phone}
            onChange={text('phone')}
          />
        </GridItem>
        <GridItem>
          <Input
            label="대표 메일"
            description="info@ 같은 회사 대표 메일만 적어요."
            value={form.email}
            onChange={text('email')}
          />
        </GridItem>
        <GridItem>
          <Input
            label="문의폼 주소"
            description="이메일이 없는 공장의 홈페이지 문의 창구."
            value={form.contactFormUrl}
            onChange={text('contactFormUrl')}
          />
        </GridItem>
        <GridItem>
          <Input
            label="공장 포털 계정 (entity_id)"
            description="이 공장의 공장 포털 계정과 이으면 그 계정이 샘플 제안·주문을 직접 봐요. 없으면 비워 두세요."
            value={form.manufacturingEntityId ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm({ ...form, manufacturingEntityId: e.target.value || null })
            }
          />
        </GridItem>
      </Grid>
      <FactoryTaxonomyEditor form={form} setForm={setForm} />
      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium">견적 분류 (견적 요청과 맞추는 데 써요)</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {PRODUCT_CATEGORY_IDS.map((id) => (
            <label key={id} className="flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={form.categoryIds.includes(id)}
                onChange={() => setForm({ ...form, categoryIds: toggle(form.categoryIds, id) })}
              />
              {productCategoryLabel(id, 'ko')}
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium">받는 보관 방식 (모르면 비워 두세요)</legend>
        <div className="flex flex-wrap gap-4">
          {QUOTE_STORAGE_TYPES.map((s) => (
            <label key={s} className="flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={form.storage.includes(s)}
                onChange={() => setForm({ ...form, storage: toggle(form.storage, s) })}
              />
              {STORAGE_LABEL[s]}
            </label>
          ))}
        </div>
      </fieldset>
      <Textarea label="만드는 품목" value={form.items} onChange={text('items')} />
      <Textarea label="운영자 메모" value={form.notes} onChange={text('notes')} />
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="primary" loading={busy} disabled={!valid} onClick={onSave}>
          {editing ? '고친 내용 저장' : '공장 더하기'}
        </Button>
        <Button variant="ghost" disabled={busy} onClick={onCancel}>
          취소
        </Button>
        {!valid && (
          <Text variant="secondary" size="sm">
            공장명은 필수예요. 나라는 KR 처럼 영문 대문자 두 글자예요.
          </Text>
        )}
      </div>
    </div>
  )
}
